// Report execution engine client (reporting foundation R4) — talks to the
// payroll-service report engine via the gateway. The server owns the entire
// vocabulary: domains, fields (already permission-filtered per viewer) and
// filter definitions. Plain employees (no reports role) 403 on every call.
import { gatewayApi } from '@/lib/hrApi';

const BASE = '/api/v1/reports';

// ---- Types --------------------------------------------------------------------

export type ReportFieldType =
  | 'string'
  | 'number'
  | 'date'
  | 'datetime'
  | 'bool'
  | 'minutes'
  | 'money';

export type ReportSource = 'live' | 'snapshot';

export interface ReportDomainSummary {
  key: string;
  label: string;
  description: string;
  source: ReportSource;
}

/** One selectable column. `sensitivity` is the server's level (INTERNAL, CONFIDENTIAL, …). */
export interface ReportField {
  key: string;
  label: string;
  type: ReportFieldType;
  sensitivity: string;
  filterable?: boolean;
  groupable?: boolean;
}

export type ReportFilterType = 'date_range' | 'select' | 'run' | 'text';

export interface ReportFilterDef {
  key: string;
  label: string;
  type: ReportFilterType;
  options?: string[];
}

/** GET /domains/{domain}/fields — fields are already filtered to the viewer. */
export interface ReportDomainFields {
  domain: string;
  label: string;
  source: ReportSource;
  fields: ReportField[];
  filters: ReportFilterDef[];
}

export interface ReportPreviewBody {
  domain: string;
  columns: string[];
  /** String-valued filters. date_range filters expand to `from`/`to` keys. */
  filters: Record<string, string>;
  /** R6: keys of `groupable: true` catalog fields. */
  group_by?: string[];
  /** R6: 'count' | 'sum:<field>' | 'avg:<field>' | 'min:<field>' | 'max:<field>' (numeric fields only). */
  aggregations?: string[];
}

/**
 * Which companies a report actually covered (multi-entity).
 *
 * Present only when the viewer is limited to specific legal entities. A report
 * narrowed to two of four companies is a wrong number presented as a right one
 * unless it says so, so this is rendered next to the row count rather than
 * tucked away.
 */
export interface ReportScopeNote {
  restricted: boolean;
  companies_included: number;
  companies_total: number;
  companies: string[] | null;
  /** Rendered server-side so the screen, the CSV and the XLSX cannot disagree. */
  label: string;
}

/** Executed report (preview capped at 200 rows, saved runs at 5000). */
export interface ReportEngineResult {
  domain: string;
  source: ReportSource;
  columns: ReportField[];
  rows: Record<string, unknown>[];
  total: number;
  truncated: boolean;
  /** Absent for a viewer who is not entity-restricted. */
  scope?: ReportScopeNote | null;
}

// ---- Client ---------------------------------------------------------------------

export const listReportDomains = (): Promise<ReportDomainSummary[]> =>
  gatewayApi
    .get<{ domains: ReportDomainSummary[] }>(`${BASE}/domains`)
    .then((r) => r.data.domains ?? []);

export const getDomainFields = (domain: string): Promise<ReportDomainFields> =>
  gatewayApi.get<ReportDomainFields>(`${BASE}/domains/${domain}/fields`).then((r) => r.data);

// Coerce a raw engine result so `columns`/`rows` are ALWAYS arrays — a backend
// response with null columns/rows would otherwise throw during React render
// (white-screen), the same class of bug the domain-fields normalization fixes.
const normalizeResult = (raw: ReportEngineResult): ReportEngineResult => ({
  ...raw,
  columns: Array.isArray(raw?.columns) ? raw.columns : [],
  rows: Array.isArray(raw?.rows) ? raw.rows : [],
  total: typeof raw?.total === 'number' ? raw.total : 0,
});

export const previewReport = (body: ReportPreviewBody): Promise<ReportEngineResult> =>
  gatewayApi.post<ReportEngineResult>(`${BASE}/preview`, body).then((r) => normalizeResult(r.data));

/**
 * Executes a SAVED definition under the viewer's permissions (the server also
 * stamps last_run_at). Optional filters override the saved ones per key.
 */
export const runSavedReport = (
  id: string,
  filters?: Record<string, string>,
): Promise<ReportEngineResult> =>
  gatewayApi
    .post<ReportEngineResult>(`${BASE}/definitions/${id}/run`, { filters: filters ?? {} })
    .then((r) => normalizeResult(r.data));

// ---- Server exports (R5) ------------------------------------------------------------
//
// POST /reports/export builds the file server-side (metadata block included)
// and returns a signed download_url. Ad-hoc exports send the full body
// (domain/columns/filters/group_by/aggregations); saved-definition exports
// send definition_id only — the server uses the stored definition.

export type ReportExportFormat = 'csv' | 'xlsx';

export interface ReportExportBody {
  /** Saved-definition export — mutually exclusive with the ad-hoc fields. */
  definition_id?: string;
  domain?: string;
  columns?: string[];
  filters?: Record<string, string>;
  group_by?: string[];
  aggregations?: string[];
  format: ReportExportFormat;
  title?: string;
}

export interface ReportExportResult {
  export_id: string;
  file_name: string;
  checksum: string;
  row_count: number;
  /** True when the export hit the 5000-row cap. */
  truncated: boolean;
  /** True when the export contains confidential columns. */
  confidential: boolean;
  download_url: string;
}

export const exportReport = (body: ReportExportBody): Promise<ReportExportResult> =>
  gatewayApi.post<ReportExportResult>(`${BASE}/export`, body).then((r) => r.data);

/** Re-issues a fresh signed download URL for a previously created export. */
export const downloadReportExport = (id: string): Promise<{ download_url: string }> =>
  gatewayApi
    .get<{ download_url: string }>(`${BASE}/exports/${id}/download`)
    .then((r) => r.data);

/** Opens a signed export URL (the server serves it as an attachment). */
export function openReportDownload(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Field types the engine accepts for sum/avg/min/max aggregations. */
export const isNumericFieldType = (type: ReportFieldType): boolean =>
  type === 'number' || type === 'minutes' || type === 'money';

// ---- Cell formatting ---------------------------------------------------------------

/**
 * Human formatting for engine cell values: minutes → "8h 15m", money stays the
 * server's string, bool → Yes/No, null/undefined/empty → '—'.
 */
export function formatCell(value: unknown, fieldType: ReportFieldType): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (fieldType) {
    case 'minutes': {
      const total = Number(value);
      if (!Number.isFinite(total)) return String(value);
      const h = Math.floor(Math.abs(total) / 60);
      const m = Math.round(Math.abs(total) % 60);
      const sign = total < 0 ? '-' : '';
      return h > 0 ? `${sign}${h}h ${m}m` : `${sign}${m}m`;
    }
    case 'bool':
      return value === true || value === 'true' || value === 1 ? 'Yes' : 'No';
    case 'money':
    default:
      return String(value);
  }
}
